// ============================================================
// Custom Questions Registry
// Central registry for all custom SurveyJS question types
//
// To add a new custom field:
// 1. Create a new folder in ./fields/YourFieldName/
// 2. Export register function and config from ./fields/YourFieldName/index.js
// 3. Import and add to CUSTOM_QUESTIONS array below
// ============================================================

import {
  registerAppointmentSetting,
  appointmentSettingConfig,
  setAppointmentChoices
} from "./fields/AppointmentSetting";

// ============================================================
// Registry of all custom questions
// Add new custom fields here
// ============================================================
export const CUSTOM_QUESTIONS = [
  {
    // Appointment Setting - Interactive dropdown for selecting appointments
    ...appointmentSettingConfig,
    registerFn: registerAppointmentSetting,
    setChoicesFn: setAppointmentChoices,
    // Add fetch function when API is ready:
    // fetchFn: () => fetch("/api/appointments").then(r => r.json())
  }
  // Add more custom questions here:
  // {
  //   id: "locationpicker",
  //   name: "locationpicker",
  //   title: "Location Picker",
  //   iconSvg: "...",
  //   defaultJson: { type: "locationpicker", ... },
  //   registerFn: registerLocationPicker,
  //   setChoicesFn: setLocationChoices,
  //   fetchFn: () => fetch("/api/locations").then(r => r.json())
  // }
];

// ============================================================
// Register all custom questions with SurveyJS
// Called once during SurveyCreator initialization
// ============================================================
export function registerAllCustomQuestions(apiDataMap = {}) {
  const results = [];

  CUSTOM_QUESTIONS.forEach(q => {
    const choices = apiDataMap[q.id] || [];

    // Set choices in the component's store
    if (q.setChoicesFn) {
      q.setChoicesFn(choices);
    }

    // Register the question type with SurveyJS
    if (q.registerFn) {
      const result = q.registerFn(choices);
      results.push({ id: q.id, ...result });
    }
  });

  console.log("[CustomQuestionsRegistry] Registered:", results.map(r => r.id).join(", "));
  return results;
}

// ============================================================
// Add all custom questions to SurveyCreator toolbox
// Called after new SurveyCreator() is instantiated
// ============================================================
export function addAllToToolbox(creator) {
  if (!creator || !creator.toolbox) {
    console.error("[CustomQuestionsRegistry] Creator or toolbox not available");
    return;
  }

  CUSTOM_QUESTIONS.forEach(q => {
    const item = creator.toolbox.addItem({
      id: q.id,
      name: q.name,
      title: q.title,
      json: q.defaultJson
    });

    // Set custom SVG icon if provided
    if (item && q.iconSvg) {
      item.svgIcon = q.iconSvg;
    }

    // console.log(`[CustomQuestionsRegistry] Added "${q.title}" to toolbox`);
  });
}

// ============================================================
// Helper to fetch data for all custom questions with APIs
// Use this when you're ready to switch from hardcoded to API data
// ============================================================
export async function fetchAllCustomQuestionData() {
  const apiDataMap = {};

  for (const q of CUSTOM_QUESTIONS) {
    if (q.fetchFn) {
      try {
        apiDataMap[q.id] = await q.fetchFn();
        // console.log(`[CustomQuestionsRegistry] Fetched data for "${q.id}"`);
      } catch (err) {
        console.error(`[CustomQuestionsRegistry] Failed to fetch "${q.id}":`, err);
        apiDataMap[q.id] = [];
      }
    }
  }

  return apiDataMap;
}

// ============================================================
// Single function to initialize everything
// Usage in SurveyCreatorWidget:
//   await initializeCustomQuestions(creator, surveyCreator);
// ============================================================
export async function initializeCustomQuestions(creator, hardcodedData = {}) {
  // 1. Register all question types with SurveyJS
  registerAllCustomQuestions(hardcodedData);

  // 2. Add all to toolbox (must happen after new SurveyCreator())
  addAllToToolbox(creator);
}
